import { Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { PageContainer, PageSectionTitle, Tile } from 'components/Layout/PageContainer';
import { useContext, useMemo } from 'react';
import { API, TenantConfigContext } from 'context/TenantConfigContext';
import { ApiTeaser } from './components/ApiTeaser';
import { TbIllustration } from 'components/Figma';

interface ApiSectionCard {
  titleId: string;
  titleDefault?: string;
  descriptionId: string;
  descriptionDefault?: string;
  iconPath: string;
  iconAlt: string;
}

const tb360ApiCards: ApiSectionCard[] = [
  {
    titleId: 'api_access.about.tb360_api.purpose.title',
    descriptionId: 'api_access.about.tb360_api.purpose.description',
    iconPath: '/icons/triangle-flag.svg',
    iconAlt: 'Purpose icon',
  },
  {
    titleId: 'api_access.about.tb360_api.benefits.title',
    descriptionId: 'api_access.about.tb360_api.benefits.description',
    iconPath: '/icons/star.svg',
    iconAlt: 'Benefits icon',
  },
  {
    titleId: 'api_access.about.tb360_api.formats.title',
    descriptionId: 'api_access.about.tb360_api.formats.description',
    iconPath: '/icons/document.svg',
    iconAlt: 'Formats icon',
  },
];

function ApiAccessAbout() {
  const { permissions } = useContext(TenantConfigContext);
  const hasApiPermission = useMemo(() => permissions.includes(API), [permissions]);
  return (
    <PageContainer>
      {!hasApiPermission && <ApiTeaser />}
      <PageSectionTitle>
        <FormattedMessage id="api_access.about.tb360_api.title" />
      </PageSectionTitle>

      <Tile>
        <div className="flex items-start gap-4">
          <TbIllustration name="ModulesHome" size="md" className="mr-1" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-15 flex-1 ">
            {tb360ApiCards.map(card => (
              <div
                key={card.titleId}
                className="flex flex-col gap-4 max-w-xs"
                data-testid={`api-about-card-${card.titleId}`}
              >
                <div className="items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <img src={card.iconPath} alt={card.iconAlt} className="w-6 h-6" />
                  </div>
                  <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    <FormattedMessage id={card.titleId} />
                  </Typography>
                </div>
                <Typography variant="body2" sx={{ color: 'primary.main', lineHeight: 1.625 }}>
                  <FormattedMessage id={card.descriptionId} />
                </Typography>
              </div>
            ))}
          </div>
        </div>
      </Tile>

      <PageSectionTitle>
        <FormattedMessage id="api_access.about.available_data.title" />
      </PageSectionTitle>

      <Tile>
        <div className="space-y-6">
          <div>
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
              <FormattedMessage id="api_access.about.available_data.insights.title" />
            </Typography>
            <Typography variant="body2" sx={{ color: 'primary.main', mb: 3 }}>
              <FormattedMessage id="api_access.about.available_data.insights.description_intro" />
            </Typography>
            <ul className="list-disc list-inside space-y-1 text-primary text-sm">
              <li>
                <FormattedMessage id="api_access.about.available_data.insights.gmv" />
              </li>
              <li>
                <FormattedMessage id="api_access.about.available_data.insights.sales_data" />
              </li>
              <li>
                <FormattedMessage
                  id="api_access.about.available_data.insights.marketplace_data"
                  defaultMessage="Marketplace Data - Performance metrics from integrated marketplaces and channels"
                />
              </li>
              <li>
                <FormattedMessage id="api_access.about.available_data.insights.order_data" />
              </li>
              <li>
                <FormattedMessage id="api_access.about.available_data.insights.product_data" />
              </li>
              <li>
                <FormattedMessage id="api_access.about.available_data.insights.returns_cancellations" />
              </li>
            </ul>
          </div>

          <div>
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
              <FormattedMessage id="api_access.about.available_data.article_visibility.title" />
            </Typography>
            <Typography variant="body2" sx={{ color: 'primary.main' }}>
              <FormattedMessage id="api_access.about.available_data.article_visibility.description" />
            </Typography>
          </div>
        </div>
      </Tile>
    </PageContainer>
  );
}

export { ApiAccessAbout };
